import styled from '@emotion/styled';
import type { TrackerTag } from '@racemap/sdk/schema/trackerTags';
import { Table, Typography } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { IconTrash } from 'src/components/Icon';
import { useStore } from 'src/store/reducers';

const { Text } = Typography;

interface TrackerTableData {
  key: React.Key;
  type: string;
  trackerId: string;
  name: string;
}
interface Props {
  selectedRowKeys: Array<React.Key>;
  setSelectedRowKeys: (selectedRowKeys: Array<React.Key>) => void;
  tag: Immutable<TrackerTag>;
  handleTrackersRemove: (trackerId: Array<string>) => void;
}

export const TagAssignedTrackersTable: FC<Props> = ({
  selectedRowKeys,
  setSelectedRowKeys,
  tag,
  handleTrackersRemove,
}) => {
  const handleSelectionChange = (newSelectedRowKeys: Array<React.Key>) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const trackers = useStore((state) => state.trackers.items);
  const rowSelection: TableRowSelection<TrackerTableData> = {
    selectedRowKeys,
    onChange: handleSelectionChange,
  };

  const dataSource: Array<TrackerTableData> = tag.trackerIds.map((trackerId, index) => ({
    index,
    key: trackerId.toHexString(),
    type: trackers.get(trackerId.toHexString())?.trackerType || '',
    trackerId: trackers.get(trackerId.toHexString())?.trackerId || '',
    name: trackers.get(trackerId.toHexString())?.trackerName || '',
  }));

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      width: 50,
      render: (index: number) => <div style={{ textAlign: 'right' }}>{index}</div>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: TrackerTableData, b: TrackerTableData) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'IMEI',
      dataIndex: 'trackerId',
      key: 'trackerId',
      render: (imei: string) => (
        <Text copyable code>
          {imei}
        </Text>
      ),
    },
    {
      title: <IconTrash />,
      dataIndex: 'delete',
      key: 'delete',
      width: 50,
      render: (_: any, record: { key: React.Key }) => (
        <DeleteButton onClick={() => handleTrackersRemove([String(record.key)])} />
      ),
    },
  ];

  return (
    <Table
      rowSelection={rowSelection}
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      scroll={{ y: 400 }}
      bordered
    />
  );
};

const DeleteButton = styled(IconTrash)`
  cursor: pointer;
`;
